/**
 * The ONLY module that knows the ICE host adapter exists.
 *
 * Everything else in this app talks to the small object this returns, which is why the whole UI
 * can be developed and tested without Encompass — and why, when the first real-host launch on
 * 2026-09-30 timed out (ICE: "Unable to launch as integration is taking longer than expected",
 * correlation 2c0741c8-f735-4baa-8e5b-ccd776f9438c), this was the only file that had to change.
 *
 * What that launch proved, against ICE's current Partner Connect reference
 * (docs.partnerconnect.elliemae.com, read 2026-09-30):
 *
 *   1. The GUEST loads ICE's library, not the other way round. A cross-origin parent cannot put
 *      globals into this frame's window; `elli` only exists if we bring it. It is bundled from
 *      npm (@elliemae/em-ssf-guest — the package behind ICE's documented
 *      cdn.elliemae.io/elliemae/core/ssf/... script tag), so `script-src 'self'` in the CSP holds.
 *   2. `elli.script.connect()` is the READY SIGNAL. The host's spinner — and that timeout dialog —
 *      wait for it. It does not return the connection.
 *   3. The objects come from `elli.script.getObject('transaction')` and `getObject('application')`.
 *      getOrigin/refreshOrigin/get/set/create/close/cancel live on transaction; performAction lives
 *      on application; there is no getDocuments anywhere — see pickDocuments below.
 */

import bundledScript from '@elliemae/em-ssf-guest';

export class HostTimeoutError extends Error {
  constructor(message) {
    super(message);
    this.name = 'HostTimeoutError';
  }
}

const DEFAULT_TIMEOUT_MS = 15000;

/**
 * @returns {Promise<{originId: string, partnerAccessToken: string, transaction: object|null,
 *                    pickDocuments: (options?: object) => Promise<Array>,
 *                    createTransaction: (req: {type: string, options: object, resources: Array}) => Promise<string>,
 *                    performAction: (name: string) => Promise<void>,
 *                    setTransaction: (id: string) => Promise<boolean>, canSetTransaction: boolean,
 *                    refreshOrigin: () => Promise<void>, close: () => Promise<void>, cancel: () => Promise<void>}>}
 */
export async function connectToHost({ timeoutMs = DEFAULT_TIMEOUT_MS, sdk } = {}) {
  // Precedence: an injected sdk (tests), then a page-installed global (the ?stubHost=1 stub, or a
  // future script-tag load), then the bundled guest library — which must come LAST or the stub
  // could never win.
  const injected = sdk?.script ?? sdk ?? globalThis.elli?.script ?? globalThis.Elli?.script;
  const script = injected ?? bundledScript;

  if (!script?.getObject && !script?.connect) {
    throw new HostTimeoutError('The Encompass host adapter is not available on this page.');
  }

  // Opened outside any frame there is no host to talk to, and the bundled library would wait out
  // the full timeout for a parent that does not exist. Saying so immediately is the common case
  // during development. A stub or injected sdk bypasses this: tests and ?stubHost=1 run unframed.
  if (!injected && globalThis.self === globalThis.top) {
    throw new HostTimeoutError('The Encompass host adapter is not available on this page.');
  }

  // The ready signal. Deliberately fire-and-forget: the host resolves its own launch dialog on
  // receiving it, and nothing in its documented contract is returned to us.
  try { script.connect(); } catch { /* a host without it still serves getObject */ }

  const transactionObj = await withTimeout(
    Promise.resolve(script.getObject('transaction')),
    timeoutMs,
    'Encompass did not respond to the connection request.',
  );

  // The application object is fetched lazily and memoized: performAction and pickDocuments are the
  // only callers, and a host that lacks it must not slow the handshake down or fail it.
  let applicationPromise = null;
  const getApplication = () => {
    applicationPromise ??= (async () => {
      try {
        return await withTimeout(
          Promise.resolve(script.getObject('application')),
          timeoutMs,
          'Encompass did not return the application object.',
        );
      } catch {
        return null;
      }
    })();
    return applicationPromise;
  };

  const origin = await withTimeout(
    Promise.resolve(transactionObj.getOrigin()),
    timeoutMs,
    'Encompass did not return the service origin.',
  );

  // The origin says whether this frame opened inside an existing transaction (transactionId is
  // "present only when launched within an existing transaction context"). transaction.get() is only
  // legal then, and even then it is allowed to fail or hang — either degrades to the id the origin
  // already gave us, so the officer is never stranded on the spinner for a nicety.
  let transaction = null;
  if (origin?.transactionId) {
    try {
      transaction = (await withTimeout(
        Promise.resolve(transactionObj.get?.()),
        timeoutMs,
        'Encompass did not return the current transaction.',
      )) ?? { id: origin.transactionId };
    } catch {
      transaction = { id: origin.transactionId };
    }
  }

  const adapter = {
    originId: origin.id,
    partnerAccessToken: origin.partnerAccessToken,
    transaction,

    // The eFolder documents for an order come from ICE's own picker, not from a list we fetch.
    // application.performAction('getAvailableResources') OPENS A MODAL in Encompass where the
    // officer browses the eFolder and picks files; it resolves to the picked resources ({id,
    // repository, name, mimeType}) or to [] when the dialog is closed without a choice, and those
    // objects go into transaction.create under $.request.resources (docs.partnerconnect.
    // elliemae.com/partnerconnect/reference/action-getavailableresources).
    //
    // So this is only ever called from the officer's own click, and three things learned in the
    // UAT sandbox on 2026-10-07 shape it:
    //   1. The action ID. We called 'getAvailableResouces' -- a typo that appears ONLY in the
    //      supported-actions list on ICE's performAction page; the action's own page, supportsAction
    //      and getCapabilities all say 'getAvailableResources'. The live click opened nothing and
    //      said nothing. The host is now asked which spelling it offers (getCapabilities, else
    //      supportsAction), preferring the documented one; with no discovery API we send that one.
    //   2. Silence. A result that is not a list (or a wrapped list) is an error the officer sees,
    //      not a quiet [] -- [] means "closed without choosing" and nothing else.
    //   3. No timeout. The officer is browsing a dialog; a 15 s clock would toast "Encompass did
    //      not return the selected documents" while they are still choosing, and lose the picks.
    // Rejections surface as a plain Error carrying the host's own message.
    pickDocuments: async (options = {}) => {
      const application = await getApplication();
      if (!application?.performAction) throw new Error('This Encompass session cannot open the eFolder.');
      const action = await resolvePickAction(application, timeoutMs);
      const request = {
        allowedSourceTypes: ['efolder'],
        allowedFileTypes: ['.pdf', '.doc', '.docx', '.tif', '.tiff', '.jpg', '.jpeg', '.png'],
        disableLocalUpload: true,
        hideUnassignedFiles: true,
        stackingTemplates: false,
        ...options,
      };
      let result;
      try {
        result = await application.performAction(action, request);
      } catch (error) {
        throw new Error(error?.message ?? 'Encompass could not open the eFolder.');
      }
      if (Array.isArray(result)) return result;
      const wrapped = result?.resources ?? result?.documents;
      if (Array.isArray(wrapped)) return wrapped;
      console.warn(`[epc] ${action} resolved to a non-list:`, result);
      throw new Error('Encompass did not open the eFolder picker.');
    },

    // The one call that places an order. ICE: transaction.create({request:{type, options,
    // resources}}) resolves to {id} and rejects with a bare {message} -- not an Error, so the
    // message has to be lifted by hand or the officer is told "[object Object]".
    //
    // The UI never creates orders through our own API: the host owns the transaction (cert 1.1),
    // and what reaches our backend is ICE's transaction.created webhook. So this rejecting is the
    // whole of "the order was not placed" -- there is no half-placed state to reconcile.
    createTransaction: async ({ type, options, resources }) => {
      if (typeof transactionObj.create !== 'function') {
        throw new Error('This Encompass session cannot create orders.');
      }
      let created;
      try {
        created = await withTimeout(
          Promise.resolve(transactionObj.create({ request: { type, options, resources } })),
          timeoutMs,
          'Encompass did not answer the order request.',
        );
      } catch (error) {
        // A timeout is genuinely different: the host may yet create the transaction, so it keeps
        // its own type and the app treats it as the unknown it is.
        if (error instanceof HostTimeoutError) throw error;
        throw new Error(error?.message ?? 'Encompass could not create the order.');
      }
      if (!created?.id) throw new Error('Encompass returned no transaction id.');
      return created.id;
    },

    // cert 1.14: how the officer is sent to re-enter their DocPrep credentials. v2 puts this on
    // the APPLICATION object (application.performAction('updateCredentials')), not the
    // transaction. Best effort, as before: a host without it is a no-op, not an error.
    performAction: async (name) => {
      const application = await getApplication();
      return application?.performAction?.(name);
    },

    // The partner access token lives 300 s. ICE documents transaction.refreshOrigin for a fresh
    // one; a host without it gets asked for the origin again, which is the same thing one call
    // older. Either way the adapter's own id and token are replaced in place, so a caller holding
    // this object sees the new session without reconnecting.
    refreshOrigin: async () => {
      const refresher = transactionObj.refreshOrigin ?? transactionObj.getOrigin;
      const fresh = await withTimeout(
        Promise.resolve(refresher.call(transactionObj)),
        timeoutMs,
        'Encompass did not refresh the service origin.',
      );
      if (fresh?.id) adapter.originId = fresh.id;
      if (fresh?.partnerAccessToken) adapter.partnerAccessToken = fresh.partnerAccessToken;
    },

    // cert 1.15 (BestPractices rows 5/27/28, multi-user editing): when the form opens on a loan that
    // already has one of our transactions, tell the host which one this iframe is showing, so
    // Encompass can warn a second officer who opens the same order. transaction.set({ id }) in v2;
    // confirmed against the sandbox at the live gate. Best effort by design -- a host without the
    // call, or one that rejects it, must not stop the officer seeing their order.
    //
    // Three failures, one answer. transaction.set may not exist; it may throw ON THE SPOT (which is
    // why the invocation is inside the try, not just the await); and it may never settle, which
    // withTimeout turns into a rejection after timeoutMs.
    //
    // `true` means only that transaction.set RESOLVED. A host that resolves with a falsy result
    // object is reported as a success, deliberately: this is best-effort telemetry, and inventing
    // a schema for the result before we have seen one would be a guess wearing a check's clothing.
    setTransaction: async (id) => {
      if (!id || typeof transactionObj.set !== 'function') return false;
      try {
        await withTimeout(
          Promise.resolve(transactionObj.set({ id })),
          timeoutMs,
          'Encompass did not acknowledge the transaction.',
        );
        return true;
      } catch {
        // Bare, deliberately: ICE rejects with a plain {message}, not an Error (see
        // createTransaction), and a binding would tempt a caller into reading .stack off it.
        return false;
      }
    },

    // Whether the Transaction object has `set` AT ALL, separate from whether a call succeeded.
    // app.js reflects this as data-host-tx="unsupported" so the sandbox gate has something to look
    // at rather than a permanent silent false. Read once at connect time, like the origin.
    canSetTransaction: typeof transactionObj.set === 'function',

    // cert 1.13: hand control back to the host. A host without the call is a no-op here, and the
    // page that hosts <epc-app> still receives the app's `close` event.
    close: async () => transactionObj.close?.(),
    cancel: async () => transactionObj.cancel?.(),
  };

  return adapter;
}

// The documented spelling first; the typo from ICE's performAction page is kept as a fallback in
// case a host build really registers it (see pickDocuments).
const PICK_ACTIONS = ['getAvailableResources', 'getAvailableResouces'];

/**
 * Which picker action ID this host offers. getCapabilities() lists supportedActions; failing that,
 * supportsAction(id) answers per ID. A host that answers neither is sent the documented ID. A host
 * that answers and lists neither spelling cannot open the picker, and the officer is told so.
 */
async function resolvePickAction(application, timeoutMs) {
  let supported = null;
  try {
    if (typeof application.getCapabilities === 'function') {
      const caps = await withTimeout(
        Promise.resolve(application.getCapabilities()),
        timeoutMs,
        'Encompass did not return its capabilities.',
      );
      if (Array.isArray(caps?.supportedActions)) supported = caps.supportedActions;
    }
    if (!supported && typeof application.supportsAction === 'function') {
      supported = [];
      for (const id of PICK_ACTIONS) {
        if (await withTimeout(Promise.resolve(application.supportsAction(id)), timeoutMs, 'Encompass did not answer supportsAction.')) {
          supported.push(id);
        }
      }
    }
  } catch {
    supported = null; // discovery itself failed: fall back to the documented ID
  }
  console.info('[epc] host supported actions:', supported ?? '(not reported)');
  if (!supported) return PICK_ACTIONS[0];
  const action = PICK_ACTIONS.find((id) => supported.includes(id));
  if (!action) throw new Error('This Encompass session does not offer the eFolder picker.');
  return action;
}

function withTimeout(promise, timeoutMs, message) {
  let timer;
  const timeout = new Promise((_, reject) => {
    timer = setTimeout(() => reject(new HostTimeoutError(message)), timeoutMs);
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
}
